import React from 'react';
import {
  VStack, HStack, Box, Text, Tooltip, TooltipContent, TooltipText,
} from '@gluestack-ui/themed';
import {
  useFormContext, FieldError, useWatch,
} from 'react-hook-form';
import SaveStatusIndicator from '@/common/components/SaveStatusIndicator';
import SimpleRTE from '@/common/components/forms/SimpleRTE';
import type { Task } from '@/common/api/models/task';
import type { EvaluationSaveStatus } from '@/lecturer/EvaluationDesk/hooks/useLecturerEvaluationController';
import DimRating from './DimRating';

interface EvaluationCardProps {
  task: Task;
  applicableDims: string[];
  evalFieldName: string;
  isAutograding: boolean;
  isSaving: boolean;
  isReadOnly: boolean;
  isDisabled: boolean;
  evaluationRequired: boolean;
  wordCountStatus: {
    currentWords: number;
    hasRequirement: boolean;
    targetWords?: number;
    isWithinRange?: boolean;
    minWords?: number;
    maxWords?: number;
    tolerancePercent?: string;
  } | null;
  isGradingRelevant: boolean;
  isDirty: boolean;
  saveStatus: EvaluationSaveStatus;
  errorMessage: string | null;
  onRetry: () => void;
}

/**
 * Unified Evaluation Card - combines ratings + feedback in one Box
 */
const EvaluationCard: React.FC<EvaluationCardProps> = ({
  task,
  applicableDims,
  evalFieldName,
  isAutograding,
  isSaving,
  isReadOnly,
  isDisabled,
  evaluationRequired,
  wordCountStatus,
  isGradingRelevant,
  isDirty,
  saveStatus,
  errorMessage,
  onRetry,
}) => {
  const { formState: { errors } } = useFormContext();
  const autoCalculateGrade = useWatch({ name: 'grading.autoCalculateGrade', defaultValue: true });
  const moduleGrade = useWatch({ name: 'grading.grade' });
  const hasOverrideWithGrade = autoCalculateGrade === false
    && moduleGrade != null
    && moduleGrade !== '';
  const feedbackRequired = isGradingRelevant && !hasOverrideWithGrade;

  // Navigate into nested form errors safely
  type TaskEvalErrors = Record<string, { feedback?: { text?: FieldError } }>;
  const taskEvaluationsErrors = errors?.taskEvaluations as TaskEvalErrors | undefined;
  const formErrors = taskEvaluationsErrors?.[task.id]?.feedback?.text;
  const dimensionsFieldName = `${evalFieldName}.dimensions`;
  const needsFeedback = task.type !== 'activity' && task.evaluation?.mode !== 'auto';

  // Strip HTML for validation
  const stripHtml = (html: string) => {
    if (!html) return '';
    return html.replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, ' ');
  };

  if (!evaluationRequired && !needsFeedback) {
    return null;
  }

  return (
    <Box
      flexDirection="column"
      bg="transparent"
      borderWidth={formErrors ? 2 : 0}
      borderColor={formErrors ? '$statusError' : 'transparent'}
      borderRadius={formErrors ? '$md' : 0}
    >
      <VStack flex={1} space="md" py="$2">

        {/* Top row: Word count + Dimension ratings + Autosave status */}
        {evaluationRequired && (
          <VStack>
            {/* Word count with label */}
            {wordCountStatus && (
              <HStack alignItems="center" mr="$4" mb="$2">
                {!wordCountStatus.hasRequirement ? (
                  <HStack alignItems="center">
                    <Text size="xs" color="$textSubtle" mr="$1">
                      Words:
                    </Text>
                    <Text size="xs" fontWeight="semibold" color="$textBody">
                      {wordCountStatus.currentWords}
                    </Text>
                  </HStack>
                ) : (
                  <Tooltip
                    placement="top"
                    openDelay={200}
                    trigger={(triggerProps) => (
                      <HStack {...triggerProps} alignItems="center">
                        <Text size="xs" color="$textSubtle" mr="$1">
                          Words:
                        </Text>
                        <Text
                          size="xs"
                          fontWeight="semibold"
                          color={
                            wordCountStatus.isWithinRange
                              ? '$statusSuccess'
                              : '$statusWarning'
                          }
                        >
                          {wordCountStatus.currentWords}
                          /
                          {wordCountStatus.targetWords}
                        </Text>
                      </HStack>
                    )}
                  >
                    <TooltipContent maxWidth={240} bg="$backgroundDark800" p="$2">
                      <TooltipText>
                        <Text color="$white" size="xs" fontWeight="semibold">
                          Word Count Requirements
                        </Text>
                        {'\n'}
                        <Text color="$textHint" size="xs">
                          Target:
                          {' '}
                          {wordCountStatus.targetWords}
                          {' '}
                          words (±
                          {wordCountStatus.tolerancePercent}
                          %)
                          {'\n'}
                          Acceptable range:
                          {' '}
                          {wordCountStatus.minWords}
                          -
                          {wordCountStatus.maxWords}
                          {' '}
                          words
                          {'\n'}
                          Current:
                          {' '}
                          {wordCountStatus.currentWords}
                          {' '}
                          words
                          {'\n'}
                          Status:
                          {' '}
                          {wordCountStatus.isWithinRange ? 'Within acceptable range' : 'Outside acceptable range'}
                        </Text>
                      </TooltipText>
                    </TooltipContent>
                  </Tooltip>
                )}
                {/* Spacer to push autosave to right */}
                <Box flex={1} />

                {/* Autosave status on the right */}
                <SaveStatusIndicator
                  status={saveStatus}
                  errorMessage={errorMessage}
                  isDirty={isDirty}
                  onRetry={onRetry}
                />
              </HStack>
            )}
            <HStack
              testID="eval-dims-row"
              w="100%"
              minWidth={0}
              alignItems="center"
              justifyContent="flex-start"
              flexWrap="nowrap"
              space="sm"
            >

              {/* Dimension ratings */}
              {applicableDims.map((dimension) => (
                <Box key={dimension} flexGrow={0} flexShrink={0}>
                  <DimRating
                    dimension={dimension}
                    evalFieldName={dimensionsFieldName}
                    isAutograding={isAutograding}
                    isSaving={isSaving}
                    isReadOnly={isReadOnly}
                  />
                </Box>
              ))}

            </HStack>
          </VStack>
        )}

        {/* Feedback section - using SimpleRTE for consistency */}
        {needsFeedback && (
          <Box mt="$2">
            <Text size="xs" color="$textSubtle" fontWeight="medium" mb="$2">
              Feedback to Student
            </Text>
            <SimpleRTE
              name={`taskEvaluations.${task.id}.feedback.text`}
              placeholder="Write feedback for the student..."
              readonly={isReadOnly || isDisabled}
              height={80}
              rules={{
                required: {
                  value: feedbackRequired,
                  message: 'Feedback is required',
                },
                validate: {
                  minTextLength: (value: string) => {
                    if (!feedbackRequired) return true;
                    const textWithoutHtml = stripHtml(value);
                    return textWithoutHtml.length >= 5 || 'At least 5 characters';
                  },
                },
              }}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default EvaluationCard;
